import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Monitor,
  Users,
  Activity,
  Receipt,
  ClipboardList,
  Coins,
  Clock,
  Shield,
  ClipboardCheck,
  SlidersHorizontal,
  Plug,
  ListChecks,
  Bell,
  BarChart3,
  Wallet,
  TrendingUp,
  Ticket,
  Layers,
  Smartphone,
  Settings as SettingsIcon,
  Flame,
  ShieldAlert,
  Search,
  CornerDownLeft,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useForceLiquidateStore } from '@/features/force-liquidate';
import { cn } from '@/lib/utils';

interface Command {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  action: (ctx: { navigate: (to: string) => void; openForceLiquidate: () => void }) => void;
  destructive?: boolean;
}

const COMMANDS: Command[] = [
  // 模块导航按侧栏分区排序。
  { id: 'nav.dashboard', group: '真实订单', label: '仪表板', hint: '/dashboard', icon: LayoutDashboard, action: (c) => c.navigate('/dashboard') },
  { id: 'nav.live', group: '真实订单', label: '资金账户大屏', hint: '/live', icon: Monitor, action: (c) => c.navigate('/live') },
  { id: 'nav.platform-pnl', group: '真实订单', label: '平台经营', hint: '/platform-pnl', icon: TrendingUp, action: (c) => c.navigate('/platform-pnl') },
  { id: 'nav.orders', group: '真实订单', label: '真实成交明细', hint: '/orders', icon: Receipt, action: (c) => c.navigate('/orders') },
  { id: 'nav.reports', group: '真实订单', label: '报表中心', hint: '/reports', icon: BarChart3, action: (c) => c.navigate('/reports') },
  { id: 'nav.employees', group: '用户管理', label: '注册用户管理', hint: '/employees', icon: Users, action: (c) => c.navigate('/employees') },
  { id: 'nav.user-orders', group: '用户管理', label: '用户订单', hint: '/user-orders', icon: ClipboardList, action: (c) => c.navigate('/user-orders') },
  { id: 'nav.signals', group: '用户管理', label: '跟单审计', hint: '/signals', icon: Activity, action: (c) => c.navigate('/signals') },
  { id: 'nav.assessment', group: '用户管理', label: '考核记录', hint: '/assessment', icon: ClipboardCheck, action: (c) => c.navigate('/assessment') },
  { id: 'nav.profit-share', group: '用户管理', label: '月度分润', hint: '/profit-share', icon: Wallet, action: (c) => c.navigate('/profit-share') },
  { id: 'nav.symbols', group: '交易配置', label: '品种与参数', hint: '/symbols', icon: Coins, action: (c) => c.navigate('/symbols') },
  { id: 'nav.session', group: '交易配置', label: '交易时段配置', hint: '/session', icon: Clock, action: (c) => c.navigate('/session') },
  { id: 'nav.trading-groups', group: '交易配置', label: '交易时段组', hint: '/trading-groups · 账户分层', icon: Layers, action: (c) => c.navigate('/trading-groups') },
  { id: 'nav.risk', group: '交易配置', label: '风控规则', hint: '/risk', icon: Shield, action: (c) => c.navigate('/risk') },
  { id: 'nav.assessment-config', group: '交易配置', label: '考核设置', hint: '/assessment-config', icon: SlidersHorizontal, action: (c) => c.navigate('/assessment-config') },
  { id: 'nav.alerts', group: '运维管理', label: '告警中心', hint: '/alerts', icon: Bell, action: (c) => c.navigate('/alerts') },
  { id: 'nav.ctids', group: '运维管理', label: 'cTID 与连接', hint: '/ctids', icon: Plug, action: (c) => c.navigate('/ctids') },
  { id: 'nav.audit', group: '运维管理', label: '审计日志', hint: '/audit-log', icon: ListChecks, action: (c) => c.navigate('/audit-log') },
  { id: 'nav.invite-codes', group: '运维管理', label: '邀请码管理', hint: '/invite-codes', icon: Ticket, action: (c) => c.navigate('/invite-codes') },
  { id: 'nav.app-version', group: '运维管理', label: 'App 版本管理', hint: '/app-version', icon: Smartphone, action: (c) => c.navigate('/app-version') },
  { id: 'nav.settings', group: '运维管理', label: '系统设置', hint: '/settings', icon: SettingsIcon, action: (c) => c.navigate('/settings') },
  // 应急
  { id: 'op.force-liquidate', group: '应急', label: '一键强制平仓', hint: '两步确认', icon: Flame, destructive: true, action: (c) => c.openForceLiquidate() },
  // 真实账户平仓 ≠ 强平:不停机、不锁交易;入口在资金账户大屏的「主账户当前持仓」卡。
  { id: 'op.detach-close', group: '应急', label: '真实账户平仓', hint: '/live 主账户持仓卡 · 不停机', icon: ShieldAlert, destructive: true, action: (c) => c.navigate('/live') },
];

/**
 * 命令面板 — Ctrl/Cmd + K 唤起 · 模糊匹配 · 上下选择 · 回车执行 · Esc 关闭
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const openForceLiquidate = useForceLiquidateStore((s) => s.open);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  // 监听 Ctrl/Cmd + K + ?  快捷键
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (e.key === '?' && !isTyping(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COMMANDS;
    return COMMANDS.filter((c) => c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered.length, highlight]);

  const exec = (cmd: Command) => {
    cmd.action({ navigate, openForceLiquidate });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0" hideClose>
        <DialogTitle className="sr-only">命令面板</DialogTitle>
        <div
          className="border-b border-line p-3"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(filtered.length - 1, h + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(0, h - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const cmd = filtered[highlight];
              if (cmd) exec(cmd);
            }
          }}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-ink-3" />
            <Input
              autoFocus
              placeholder="跳转或执行 · 输入页面名 / 路径 / 'force-liquidate' ..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              className="pl-8 border-0 focus-visible:ring-0 shadow-none bg-transparent"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin p-1">
          {filtered.length === 0 && <div className="p-6 text-center text-sm text-ink-3">无匹配项</div>}
          {filtered.map((cmd, i) => (
            <button
              key={cmd.id}
              type="button"
              onClick={() => exec(cmd)}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'w-full text-left rounded-md px-3 py-2 flex items-center gap-3 text-sm transition-colors',
                i === highlight ? 'bg-bg-3' : 'hover:bg-bg-3',
                cmd.destructive && 'text-down',
              )}
            >
              <cmd.icon className="h-4 w-4 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{cmd.label}</div>
                {cmd.hint && <div className="text-xs text-ink-3 num truncate">{cmd.hint}</div>}
              </div>
              <span className="text-[10px] uppercase tracking-wider text-ink-3">{cmd.group}</span>
              {i === highlight && <CornerDownLeft className="h-3.5 w-3.5 text-ink-3" />}
            </button>
          ))}
        </div>
        <div className="border-t border-line px-3 py-2 flex items-center gap-3 text-[11px] text-ink-3 num">
          <span>↑↓ 移动</span>
          <span>↵ 执行</span>
          <span>Esc 关闭</span>
          <span className="ml-auto">Ctrl/⌘ K · ? 唤起</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function isTyping(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable;
}
